import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BellRing,
  CalendarCheck,
  CalendarClock,
  ChevronLeft,
  MapPin,
  Moon,
  Sun,
  Sunrise,
  User,
  type LucideIcon,
} from 'lucide-react';
import {
  DAY_PARTS,
  zonedToday,
  type DayPart,
  type PublicBookingPage,
  type PublicCondition,
  type PublicLocation,
  type PublicService,
  type WaitlistJoinResult,
} from '@rx-manager/shared';
import { BookingApiError, waitlistApi } from '../../lib/api';
import { useI18n, type MessageKey } from '../../lib/i18n';
import { dobProblem, isEmail, isUsablePhone } from '../../lib/validate';
import {
  Alert,
  Button,
  CHECKBOX,
  Field,
  HEADING,
  IconTile,
  Input,
  StepActions,
  SuccessMark,
  buttonClasses,
  cn,
} from '../../components/ui';
import { Detail } from '../../components/AppointmentCard';
import { Turnstile } from '../forms/Turnstile';
import { TextRemindersBox } from '../booking/shared';

/**
 * Track BCW — joining a service's waitlist (BCW-D9, D10, D12), from the time step ("None of these
 * work?") or as the only way to book a waitlist service.
 *
 * Two screens and a done screen: when they can come (a branch or any, days, parts of the day, how
 * long), then who they are and how to reach them (a mobile, an email, or both; texts only with the
 * box ticked, in the page's consent wording). No time is held. Nothing is kept in the browser: the
 * place's link comes back from the server for the done screen, and goes out by email.
 */

type Stage = 'when' | 'who';
type Errors = Partial<Record<'firstName' | 'lastName' | 'dob' | 'phone' | 'email' | 'contact', MessageKey>>;

/** `YYYY-MM-DD` plus days, on the calendar alone. */
function plusDays(ymd: string, days: number): string {
  const at = new Date(`${ymd}T12:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

/** ISO weekday names, Monday first, in the page's language. */
function weekdayNames(locale: string): string[] {
  const format = new Intl.DateTimeFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', { weekday: 'long', timeZone: 'UTC' });
  // 2024-01-01 was a Monday.
  return Array.from({ length: 7 }, (_, i) => format.format(new Date(Date.UTC(2024, 0, 1 + i, 12))));
}

export function WaitlistJoin({
  page,
  service,
  condition,
  locations,
  waitlistOnly,
  runsOn,
  onBack,
}: {
  page: PublicBookingPage;
  service: PublicService;
  condition: PublicCondition | null;
  /** The branches that deliver the service. */
  locations: PublicLocation[];
  /** The service is booked only this way: say so. */
  waitlistOnly: boolean;
  /**
   * Track CGS (CGS-D19) — at a clinic, the ISO weekdays it still runs on: only those are offered,
   * and none is asked for when it runs on one, since a place for another day could never be offered.
   */
  runsOn?: number[];
  onBack: () => void;
}) {
  const { t, locale } = useI18n();
  const slug = page.profile.slug;
  const maxDays = page.waitlist?.maxDays ?? 30;
  const [stage, setStage] = useState<Stage>('when');
  const [locationId, setLocationId] = useState(locations.length === 1 ? locations[0]!.id : '');
  const [days, setDays] = useState<number[]>([]);
  const [parts, setParts] = useState<DayPart[]>([]);
  const zone = (locations.find((l) => l.id === locationId) ?? locations[0])?.timezone ?? 'America/Toronto';
  const today = zonedToday(zone);
  const [endsOn, setEndsOn] = useState(() => plusDays(today, maxDays));
  const [patient, setPatient] = useState({ firstName: '', lastName: '', dob: '', email: '', phone: '' });
  const [texts, setTexts] = useState(false);
  const [website, setWebsite] = useState('');
  const [turnstile, setTurnstile] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState<WaitlistJoinResult | null>(null);
  const names = useMemo(() => weekdayNames(locale), [locale]);

  const textOffer = page.waitlist?.textOffers ? (page.textReminders ?? null) : null;
  const siteKey = page.waitlist?.turnstileSiteKey ?? null;

  const toggle = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  const join = async (): Promise<void> => {
    const next: Errors = {};
    if (!patient.firstName.trim()) next.firstName = 'error.fieldRequired';
    if (!patient.lastName.trim()) next.lastName = 'error.fieldRequired';
    const dob = dobProblem(patient.dob);
    if (dob) next.dob = dob;
    if (patient.phone.trim() && !isUsablePhone(patient.phone)) next.phone = 'error.phoneInvalid';
    if (patient.email.trim() && !isEmail(patient.email)) next.email = 'error.emailInvalid';
    if (!patient.phone.trim() && !patient.email.trim()) next.contact = 'waitlist.contactRequired';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setBusy(true);
    setFailure(null);
    try {
      const result = await waitlistApi.join(slug, {
        serviceId: service.id,
        ...(locationId ? { locationId } : {}),
        ...(condition ? { conditionId: condition.id } : {}),
        days: [...days].sort((a, b) => a - b),
        parts: DAY_PARTS.filter((p) => parts.includes(p)),
        endsOn,
        patient: {
          firstName: patient.firstName.trim(),
          lastName: patient.lastName.trim(),
          dob: patient.dob,
          ...(patient.email.trim() ? { email: patient.email.trim() } : {}),
          ...(patient.phone.trim() ? { phone: patient.phone.trim() } : {}),
        },
        ...(textOffer && patient.phone.trim() ? { textOffers: texts } : {}),
        language: locale,
        ...(website ? { website } : {}),
        ...(turnstile ? { turnstile } : {}),
      });
      setJoined(result);
    } catch (error) {
      setFailure(error instanceof BookingApiError ? error.message : t('error.offline'));
    } finally {
      setBusy(false);
    }
  };

  if (joined) {
    const where = joined.location?.name ?? t('waitlist.anyBranch');
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center gap-4 pt-2 text-center">
          <SuccessMark />
          <h1 tabIndex={-1} className={HEADING}>
            {t('waitlist.doneHeading')}
          </h1>
          <p className="max-w-md text-base text-muted-foreground">
            {joined.already ? t('waitlist.already') : t('waitlist.doneLead', { service: joined.serviceTitle })}
          </p>
        </div>
        <dl className="divide-y divide-border rounded-xl border border-border px-4 sm:px-5">
          <Detail icon={MapPin} label={t('waitlist.where')} value={where} />
          <Detail icon={CalendarClock} label={t('waitlist.when')} value={wantedText(joined.days, joined.parts, names, t)} />
          <Detail icon={CalendarCheck} label={t('waitlist.endsOn')} value={joined.endsOn} />
        </dl>
        {joined.offersBy && (
          <div className="flex items-start gap-3 rounded-xl border border-primary/20 bg-accent/60 p-4">
            <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-primary-strong" aria-hidden="true" />
            <div className="space-y-1">
              <p className="text-base font-semibold text-heading">{t(`waitlist.by.${joined.offersBy}`)}</p>
              <p className="text-sm text-muted-foreground">{t('waitlist.holdNote')}</p>
            </div>
          </div>
        )}
        <Link to={`/w/${joined.entryToken}`} className={buttonClasses('outline', 'md')}>
          {t('waitlist.manage')}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 tabIndex={-1} className={HEADING}>
          {t('waitlist.heading', { service: service.title })}
        </h1>
        <p className="text-base text-muted-foreground">{waitlistOnly ? t('waitlist.onlyLead') : t('waitlist.lead')}</p>
      </div>
      {failure && <Alert>{failure}</Alert>}

      {stage === 'when' ? (
        <div className="space-y-6">
          {locations.length > 1 && (
            <fieldset className="space-y-3">
              <legend className={LEGEND}>{t('waitlist.branch')}</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                {[{ id: '', name: t('waitlist.anyBranch') }, ...locations].map((location) => (
                  <label key={location.id || 'any'} className={tile(locationId === location.id)}>
                    <input
                      type="radio"
                      name="waitlist-branch"
                      checked={locationId === location.id}
                      onChange={() => setLocationId(location.id)}
                      className={CHECKBOX}
                    />
                    <span className="text-base font-medium text-heading">{location.name}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {(!runsOn || runsOn.length > 1) && (
            <fieldset className="space-y-3">
              <legend className={LEGEND}>{t('waitlist.days')}</legend>
              <p className="text-sm text-muted-foreground">{t('waitlist.daysHelp')}</p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {names.map((name, index) =>
                  runsOn && !runsOn.includes(index + 1) ? null : (
                    <label key={name} className={tile(days.includes(index + 1))}>
                      <input
                        type="checkbox"
                        checked={days.includes(index + 1)}
                        onChange={() => setDays(toggle(days, index + 1))}
                        className={CHECKBOX}
                      />
                      <span className="text-base font-medium capitalize text-heading">{name}</span>
                    </label>
                  ),
                )}
              </div>
            </fieldset>
          )}

          <fieldset className="space-y-3">
            <legend className={LEGEND}>{t('waitlist.parts')}</legend>
            <p className="text-sm text-muted-foreground">{t('waitlist.partsHelp')}</p>
            <div className="grid gap-2 sm:grid-cols-3">
              {DAY_PARTS.map((part) => {
                const Icon = PART_ICON[part];
                return (
                  <label key={part} className={tile(parts.includes(part))}>
                    <input
                      type="checkbox"
                      checked={parts.includes(part)}
                      onChange={() => setParts(toggle(parts, part))}
                      className={CHECKBOX}
                    />
                    <span className="flex items-center gap-2 text-base font-medium text-heading">
                      <Icon className="h-4 w-4 shrink-0 text-primary-strong" aria-hidden="true" />
                      {t(`waitlist.part.${part}`)}
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <div className="sm:w-1/2 sm:pr-2.5">
            <Field id="waitlist-until" label={t('waitlist.until')} help={t('waitlist.untilHelp', { days: maxDays })}>
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={endsOn}
                  min={today}
                  max={plusDays(today, maxDays)}
                  onChange={(event) => setEndsOn(event.target.value)}
                />
              )}
            </Field>
          </div>

          <StepActions>
            <Button variant="outline" onClick={onBack}>
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              {t('step.back')}
            </Button>
            <Button onClick={() => setStage('who')} className="flex-1 sm:flex-none">
              {t('step.continue')}
            </Button>
          </StepActions>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex items-start gap-3">
            <IconTile icon={User} size="sm" />
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-heading">{t('waitlist.contactHeading')}</h2>
              <p className="text-sm text-muted-foreground">{t('waitlist.contactHelp')}</p>
            </div>
          </div>
          {errors.contact && <Alert>{t(errors.contact)}</Alert>}
          <div className="grid gap-5 sm:grid-cols-2">
            <Field id="wl-first" label={t('details.firstName')} error={errors.firstName && t(errors.firstName)} required>
              {(props) => (
                <Input
                  {...props}
                  autoComplete="given-name"
                  value={patient.firstName}
                  onChange={(e) => setPatient({ ...patient, firstName: e.target.value })}
                />
              )}
            </Field>
            <Field id="wl-last" label={t('details.lastName')} error={errors.lastName && t(errors.lastName)} required>
              {(props) => (
                <Input
                  {...props}
                  autoComplete="family-name"
                  value={patient.lastName}
                  onChange={(e) => setPatient({ ...patient, lastName: e.target.value })}
                />
              )}
            </Field>
          </div>
          <div className="sm:w-1/2 sm:pr-2.5">
            <Field id="wl-dob" label={t('details.dob')} error={errors.dob && t(errors.dob)} required>
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  autoComplete="bday"
                  value={patient.dob}
                  onChange={(e) => setPatient({ ...patient, dob: e.target.value })}
                />
              )}
            </Field>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field id="wl-phone" label={t('details.phone')} error={errors.phone && t(errors.phone)}>
              {(props) => (
                <Input
                  {...props}
                  type="tel"
                  autoComplete="tel"
                  value={patient.phone}
                  onChange={(e) => setPatient({ ...patient, phone: e.target.value })}
                />
              )}
            </Field>
            <Field id="wl-email" label={t('details.email')} error={errors.email && t(errors.email)}>
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  autoComplete="email"
                  value={patient.email}
                  onChange={(e) => setPatient({ ...patient, email: e.target.value })}
                />
              )}
            </Field>
          </div>
          {textOffer && patient.phone.trim() && (
            <TextRemindersBox
              wordings={textOffer.wordings}
              checked={texts}
              onChange={setTexts}
              label={t('waitlist.textOffers')}
            />
          )}
          {/* A field a person never sees, so never fills in. */}
          <div className={cn('absolute -left-[9999px] h-0 w-0 overflow-hidden')} aria-hidden="true">
            <label htmlFor="wl-website">Website</label>
            <input
              id="wl-website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>
          {siteKey && <Turnstile siteKey={siteKey} language={locale} onToken={setTurnstile} />}
          <StepActions>
            <Button variant="outline" onClick={() => setStage('when')}>
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              {t('step.back')}
            </Button>
            <Button onClick={() => void join()} loading={busy} className="flex-1 sm:flex-none">
              {t('waitlist.join')}
            </Button>
          </StepActions>
        </div>
      )}
    </div>
  );
}

const LEGEND = 'text-base font-semibold text-heading';

const PART_ICON: Record<DayPart, LucideIcon> = { morning: Sunrise, afternoon: Sun, evening: Moon };

/** A box or a round choice as a whole tile, emerald once chosen. */
function tile(chosen: boolean): string {
  return cn(
    'flex min-h-12 cursor-pointer items-center gap-3 rounded-xl border px-3.5 py-2.5 transition-colors',
    chosen ? 'border-primary bg-accent/70' : 'border-input bg-card hover:border-primary/60',
  );
}

/** "Tuesday, Thursday · Afternoon" — or "Any time". */
export function wantedText(
  days: number[],
  parts: DayPart[],
  names: string[],
  t: (key: MessageKey) => string,
): string {
  if (days.length === 0 && parts.length === 0) return t('waitlist.anyTime');
  const dayText = days.map((d) => names[d - 1]).join(', ');
  const partText = parts.map((p) => t(`waitlist.part.${p}` as MessageKey)).join(', ');
  return [dayText, partText].filter(Boolean).join(' · ');
}
